/**
 * Pistes tracées par le joueur : points de passage -> tracé, contrôle
 * (pente, obstacles, raccordements), coût et durée des travaux.
 *
 * Partagé : le client évalue le brouillon à chaque mouvement de souris pour
 * l'aperçu, le serveur le réévalue à la réception de la commande. Le verdict
 * est donc le même des deux côtés.
 */
import type { BuildingKind } from './catalog';
import { chaikin, dist2, distPointPolyline, resample, type Vec2 } from './geometry';
import type { GameMap } from './map';
import { DIFFICULTY_LABEL, PISTE_SIZE_FACTOR, TARGET_GRADE, analysePiste, generatePistePath, pisteWidth, type PisteAnalysis } from './pistes';
import { MAX_UPHILL } from './planning';
import { NET, parsePisteNode, type Anchor, type PisteNetwork } from './network';
import type { EntityId, PisteRoute, PisteSize } from './state';
import type { Heightfield } from './terrain';
import { buildingRadius } from './validation';

export const PISTE_DESIGN = {
  /** Points de passage au plus (départ et arrivée compris). */
  maxPoints: 24,
  minLength: 120,
  maxLength: 6000,
  /** Pente du 90e centile (sur 40 m) au-delà de laquelle même une noire n'est pas damable. */
  maxP90: 0.78,
  /** Passage le plus raide toléré (sur 40 m). */
  maxSlope: 1,
  /** Coût : forfait + mètre linéaire (déboisement en forêt en plus), selon le gabarit. */
  base: 6_000,
  perMeter: 30,
  forestPerMeter: 22,
  /** Reprise d'un tracé : part du coût d'une piste neuve. */
  editShare: 0.6,
  editMin: 2_000,
  /** Travaux : heures de jeu, forfait + par km, selon le gabarit. */
  hoursBase: 3,
  hoursPerKm: 5,
} as const;

/**
 * Refus d'une reprise ou d'une démolition tant que des skieurs sont sur la
 * piste ou s'y rendent : le client ferme alors la piste et réessaie.
 */
export const PISTE_BUSY = 'Des skieurs sont encore sur la piste';

/** Motif affiché d'une piste en travaux (création ou reprise du tracé). */
export const PISTE_WORKS = 'En construction';

export const PISTE_ROUTES: readonly PisteRoute[] = ['direct', 'verte', 'bleue', 'rouge', 'noire'];

export const PISTE_ROUTE_LABEL: Record<PisteRoute, string> = { direct: 'Direct', ...DIFFICULTY_LABEL };

export interface PisteDraft {
  points: Vec2[];
  route: PisteRoute;
  size: PisteSize;
}

export interface PisteDesignContext {
  hf: Heightfield;
  map: GameMap;
  /** Réseau actuel (raccordements du départ et de l'arrivée). */
  net: PisteNetwork;
  lifts: Iterable<{ id: EntityId; bottom: Vec2; top: Vec2 }>;
  buildings: Iterable<{ id: EntityId; kind: BuildingKind; x: number; z: number }>;
  cash?: number;
  /**
   * Cache des tronçons entre deux points de passage (aperçu client : seul le
   * tronçon qui suit le curseur est recalculé). Le résultat est identique.
   */
  segCache?: Map<string, Vec2[]>;
}

export type PisteCheck =
  | {
      ok: true;
      cost: number;
      hours: number;
      path: Vec2[];
      /** Points de passage retenus (départ et arrivée aimantés). */
      points: Vec2[];
      analysis: PisteAnalysis;
      width: number;
      from: Anchor;
      to: Anchor;
    }
  | {
      ok: false;
      reason: string;
      cost: number;
      path?: Vec2[];
      analysis?: PisteAnalysis;
      width?: number;
      from?: Anchor | null;
      to?: Anchor | null;
    };

const round1 = (p: Vec2): Vec2 => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10];

/** Part du tracé sous la limite des arbres (déboisement). */
export function forestShare(hf: Heightfield, map: GameMap, path: readonly Vec2[]): number {
  const pts = resample(path, 20);
  let n = 0;
  for (const p of pts) if (hf.height(p[0], p[1]) < map.treeLine) n++;
  return pts.length ? n / pts.length : 0;
}

export function pisteCost(length: number, size: PisteSize, forest = 0): number {
  const perMeter = PISTE_DESIGN.perMeter + PISTE_DESIGN.forestPerMeter * forest;
  return Math.round((PISTE_DESIGN.base + length * perMeter * PISTE_SIZE_FACTOR[size]) / 100) * 100;
}

/** Coût d'une reprise de tracé (élargissement, nouveau passage). */
export function pisteEditCost(length: number, size: PisteSize, forest = 0): number {
  return Math.max(PISTE_DESIGN.editMin, Math.round((pisteCost(length, size, forest) * PISTE_DESIGN.editShare) / 100) * 100);
}

/** Durée des travaux (heures de jeu). */
export function pisteHours(length: number, size: PisteSize, editing = false): number {
  const h = (PISTE_DESIGN.hoursBase + (length / 1000) * PISTE_DESIGN.hoursPerKm) * PISTE_SIZE_FACTOR[size];
  return Math.round(h * (editing ? PISTE_DESIGN.editShare : 1) * 10) / 10;
}

/** Courbe de Catmull-Rom centripète passant par chaque point (pas de `step` m). */
function catmullRom(points: readonly Vec2[], step: number): Vec2[] {
  const n = points.length;
  if (n < 3) return points.map((p) => [p[0], p[1]] as Vec2);
  const at = (i: number): Vec2 => {
    if (i < 0) {
      const a = points[0] as Vec2;
      const b = points[1] as Vec2;
      return [2 * a[0] - b[0], 2 * a[1] - b[1]];
    }
    if (i >= n) {
      const a = points[n - 1] as Vec2;
      const b = points[n - 2] as Vec2;
      return [2 * a[0] - b[0], 2 * a[1] - b[1]];
    }
    return points[i] as Vec2;
  };
  const knot = (a: Vec2, b: Vec2) => Math.max(1e-3, Math.sqrt(dist2(a, b)));
  const lerp = (a: Vec2, b: Vec2, ta: number, tb: number, t: number): Vec2 => {
    const d = tb - ta || 1e-6;
    const u = (t - ta) / d;
    return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u];
  };
  const out: Vec2[] = [[(points[0] as Vec2)[0], (points[0] as Vec2)[1]]];
  for (let i = 0; i < n - 1; i++) {
    const p0 = at(i - 1);
    const p1 = at(i);
    const p2 = at(i + 1);
    const p3 = at(i + 2);
    const t0 = 0;
    const t1 = t0 + knot(p0, p1);
    const t2 = t1 + knot(p1, p2);
    const t3 = t2 + knot(p2, p3);
    const k = Math.max(1, Math.ceil(dist2(p1, p2) / step));
    for (let j = 1; j <= k; j++) {
      const t = t1 + ((t2 - t1) * j) / k;
      const a1 = lerp(p0, p1, t0, t1, t);
      const a2 = lerp(p1, p2, t1, t2, t);
      const a3 = lerp(p2, p3, t2, t3, t);
      const b1 = lerp(a1, a2, t0, t2, t);
      const b2 = lerp(a2, a3, t1, t3, t);
      out.push(lerp(b1, b2, t1, t2, t));
    }
  }
  return out;
}

/**
 * Tracé passant par les points de passage. « Direct » : courbe lissée qui
 * passe par chaque point. Une difficulté : entre deux points, le tracé
 * traverse la pente pour tenir la pente visée (comme les pistes générées).
 */
export function routePistePath(
  hf: Heightfield,
  points: readonly Vec2[],
  route: PisteRoute,
  avoid: { a: Vec2; b: Vec2; clearance: number }[] = [],
  segCache?: Map<string, Vec2[]>,
): Vec2[] {
  if (points.length < 2) return points.map(round1);
  let pts: Vec2[];
  if (route === 'direct') {
    pts = catmullRom(points, 6);
  } else {
    const grade = TARGET_GRADE[route];
    const avoidKey = segCache ? avoid.map((v) => `${v.a[0]},${v.a[1]},${v.b[0]},${v.b[1]},${v.clearance}`).join(';') : '';
    pts = [];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i] as Vec2;
      const b = points[i + 1] as Vec2;
      const key = segCache ? `${route}|${i}|${a[0]},${a[1]}|${b[0]},${b[1]}|${avoidKey}` : '';
      let seg = segCache?.get(key);
      if (!seg) {
        let best: { path: Vec2[]; score: number } | null = null;
        for (const side of [1, -1] as const) {
          const path = dist2(a, b) < 30 ? [a, b] : generatePistePath(hf, a, b, { side, grade, seed: i * 2 + (side > 0 ? 1 : 0), avoid });
          const an = analysePiste(hf, path);
          const score = an.uphill * 2 + Math.abs(an.avgSlope - grade) * 10;
          if (!best || score < best.score) best = { path, score };
        }
        seg = (best as { path: Vec2[] }).path;
        if (segCache) {
          if (segCache.size > 400) segCache.clear();
          segCache.set(key, seg);
        }
      }
      pts.push(...(i > 0 ? seg.slice(1) : seg));
    }
    pts = chaikin(pts, 1);
  }
  const out = resample(pts, 14).map(round1);
  out[0] = round1(points[0] as Vec2);
  out[out.length - 1] = round1(points[points.length - 1] as Vec2);
  return out;
}

/** Simplification (Douglas-Peucker) d'un tracé généré, pour en éditer les points de passage. */
export function simplifyPath(path: readonly Vec2[], tolerance = 10, maxPoints = 12): Vec2[] {
  if (path.length <= 2) return path.map(round1);
  let tol = tolerance;
  for (let iter = 0; iter < 8; iter++) {
    const keep = new Uint8Array(path.length);
    keep[0] = 1;
    keep[path.length - 1] = 1;
    const stack: [number, number][] = [[0, path.length - 1]];
    while (stack.length) {
      const [i, j] = stack.pop() as [number, number];
      let worst = -1;
      let worstD = tol;
      for (let k = i + 1; k < j; k++) {
        const d = distPointPolyline(path[k] as Vec2, [path[i] as Vec2, path[j] as Vec2]);
        if (d > worstD) {
          worstD = d;
          worst = k;
        }
      }
      if (worst > 0) {
        keep[worst] = 1;
        stack.push([i, worst], [worst, j]);
      }
    }
    const out = path.filter((_, k) => keep[k]).map(round1);
    if (out.length <= maxPoints) return out;
    tol *= 1.5;
  }
  return [round1(path[0] as Vec2), round1(path[path.length - 1] as Vec2)];
}

/** Points de passage éditables d'une piste (ceux du joueur, sinon tirés du tracé). */
export function editablePoints(p: { path: Vec2[]; waypoints?: Vec2[] }): Vec2[] {
  return p.waypoints && p.waypoints.length >= 2 ? p.waypoints.map((q) => [q[0], q[1]] as Vec2) : simplifyPath(p.path);
}

/**
 * Contrôle d'un brouillon de piste (création, ou reprise de la piste
 * `editing`) : raccordements, tracé, pente, obstacles, fonds.
 */
export function checkPiste(ctx: PisteDesignContext, draft: PisteDraft, editing?: EntityId): PisteCheck {
  const { hf, map, net } = ctx;
  const lifts = [...ctx.lifts];
  const buildings = [...ctx.buildings];
  const raw = draft.points;
  const fail = (reason: string, extra: Partial<Extract<PisteCheck, { ok: false }>> = {}): PisteCheck => ({ ok: false, reason, cost: 0, ...extra });
  if (raw.length < 2) return fail('Placez un départ puis une arrivée');
  if (raw.length > PISTE_DESIGN.maxPoints) return fail(`${PISTE_DESIGN.maxPoints} points de passage au plus`);
  const from = net.anchorAt(raw[0] as Vec2, 'start', editing);
  if (!from) return fail('Le départ doit être au sommet d\u2019une remontée, à une gare ou sur une piste', { from: null });
  const to = net.anchorAt(raw[raw.length - 1] as Vec2, 'end', editing);
  if (!to) return fail('L\u2019arrivée doit rejoindre une gare aval, une piste ou le village', { from, to: null });
  if (from.node === to.node) return fail('Le départ et l\u2019arrivée se rejoignent au même endroit', { from, to });
  // Points intermédiaires trop proches : un seul suffit.
  const points: Vec2[] = [from.point];
  for (const p of raw.slice(1, -1)) if (dist2(points[points.length - 1] as Vec2, p) > 8) points.push(p);
  if (dist2(points[points.length - 1] as Vec2, to.point) < 8 && points.length > 1) points.pop();
  points.push(to.point);
  const avoid = lifts.map((l) => ({ a: l.bottom, b: l.top, clearance: 22 }));
  const path = routePistePath(hf, points, draft.route, avoid, ctx.segCache);
  const analysis = analysePiste(hf, path);
  const width = pisteWidth(analysis.difficulty, draft.size);
  const forest = forestShare(hf, map, path);
  const cost = editing !== undefined ? pisteEditCost(analysis.length, draft.size, forest) : pisteCost(analysis.length, draft.size, forest);
  const bad = (reason: string): PisteCheck => ({ ok: false, reason, cost, path, analysis, width, from, to });
  for (const p of path) if (!hf.inBounds(p[0], p[1], 20)) return bad('Hors de la carte');
  if (analysis.length < PISTE_DESIGN.minLength) return bad(`Trop courte (${PISTE_DESIGN.minLength} m au moins)`);
  if (analysis.length > PISTE_DESIGN.maxLength) return bad('Trop longue (6 km au plus)');
  if (analysis.uphill > MAX_UPHILL) return bad('Le tracé remonte : une piste descend toujours');
  if (analysis.p90Slope > PISTE_DESIGN.maxP90 || analysis.maxSlope > PISTE_DESIGN.maxSlope) return bad('Trop raide par endroits, même pour une noire');
  const lake = map.features.lake;
  if (distPointPolyline([lake.x, lake.z], path) < lake.radius + width / 2) return bad('Le tracé traverse le lac');
  for (const b of buildings) {
    if (b.kind === 'lamp_post' || b.kind === 'snow_cannon') continue;
    if (distPointPolyline([b.x, b.z], path) < buildingRadius(b.kind) + width / 2 - 1) return bad('Une construction gêne le tracé');
  }
  // Passer à une gare est permis : le réseau en fait un arrêt (gare aval) ou un accès (sommet).
  if (editing !== undefined) {
    // Les pistes raccordées à celle-ci doivent toujours la toucher.
    for (const info of net.pistes.values()) {
      if (info.piste.id === editing) continue;
      for (const [key, pt] of [
        [info.from, info.piste.path[0]],
        [info.to, info.piste.path[info.piste.path.length - 1]],
      ] as const) {
        if (parsePisteNode(key)?.piste !== editing || !pt) continue;
        if (distPointPolyline(pt, path) > width / 2 + NET.pisteMargin) return bad(`Le nouveau tracé ne passe plus par le raccord de ${info.piste.name}`);
      }
    }
  }
  if (ctx.cash !== undefined && ctx.cash < cost) return bad('Fonds insuffisants');
  return { ok: true, cost, hours: pisteHours(analysis.length, draft.size, editing !== undefined), path, points: points.map(round1), analysis, width, from, to };
}
